/**
 * Configurações → «CRM do Odoo»: equipas de onde vêm as leads do simulador
 * (Prospeção + Vendas), equipa para onde vai a oportunidade ganha e etapa
 * da proposta enviada. «Automático» = pelo nome no Odoo (Prospeção, Vendas,
 * Propostas Entregues).
 */
import { useEffect, useState } from "react"
import type { AppShell } from "@/App"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { detectOdooIntegrationUser, getOdooCrmOptions, type OdooCrmConfig, type OdooCrmOptions } from "@/lib/api"

const AUTO = "__auto__"

export function OdooCrmCard({
  shell, value, onChange,
}: {
  shell: AppShell | null
  value: OdooCrmConfig
  onChange: (next: OdooCrmConfig) => void
}) {
  const [options, setOptions] = useState<OdooCrmOptions | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!shell) return
    let cancelled = false
    getOdooCrmOptions(shell)
      .then((o) => { if (!cancelled) setOptions(o) })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : "Falha a ler o Odoo") })
    return () => { cancelled = true }
  }, [shell])

  const [detecting, setDetecting] = useState(false)
  const [detectError, setDetectError] = useState<string | null>(null)

  async function detectAgain() {
    if (!shell) return
    setDetecting(true)
    setDetectError(null)
    try {
      const who = await detectOdooIntegrationUser(shell)
      // Já ficou gravado no servidor; o rascunho acompanha para um «Guardar» não o apagar.
      onChange({ ...value, integration_user_id: who.user_id, integration_partner_id: who.partner_id, integration_user_name: who.name })
    } catch (e) {
      setDetectError(e instanceof Error ? e.message : "Falha a detetar no Odoo")
    } finally {
      setDetecting(false)
    }
  }

  const teamName = (id: number | null | undefined) => options?.teams.find((t) => t.id === id)?.name
  const stageName = (id: number | null | undefined) => options?.stages.find((s) => s.id === id)?.name

  function field(
    key: "prospect_team_id" | "sales_team_id" | "proposal_stage_id",
    label: string,
    help: string,
    items: Array<{ id: number; name: string }>,
    autoName: string | undefined,
  ) {
    const current = value[key]
    return (
      <div className="space-y-1">
        <Label>{label}</Label>
        <Select
          value={current ? String(current) : AUTO}
          onValueChange={(v) => {
            const next = { ...value }
            if (v === AUTO) delete next[key]
            else next[key] = Number(v)
            onChange(next)
          }}
          disabled={!options}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={AUTO}>Automático{autoName ? ` (${autoName})` : " (não encontrada)"}</SelectItem>
            {items.map((i) => (
              <SelectItem key={i.id} value={String(i.id)}>{i.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{help}</p>
      </div>
    )
  }

  // «Automático» mostra o que o nome encontra — sem o override das Configurações.
  const auto = options?.resolved
  return (
    <Card>
      <CardHeader>
        <CardTitle>CRM do Odoo</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          A pesquisa de leads dos assistentes procura nas equipas de prospeção e de vendas do Odoo.
          Ao enviar a proposta por email, a lead passa à etapa escolhida; no «Ganho», a oportunidade
          passa à equipa de vendas, com o comercial, o cliente e o valor s/ IVA, e fica ganha.
          «Perdido» não mexe no Odoo. O seguimento das propostas é lido das atividades, notas e etapas da lead.
        </p>
        {error && <p className="text-xs text-destructive">{error}</p>}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {field("prospect_team_id", "Equipa de prospeção", "Leads criadas pelos comerciais/SDR.",
            options?.teams ?? [], value.prospect_team_id ? undefined : teamName(auto?.prospectTeamId))}
          {field("sales_team_id", "Equipa de vendas", "Também pesquisada; recebe as oportunidades ganhas.",
            options?.teams ?? [], value.sales_team_id ? undefined : teamName(auto?.salesTeamId))}
          {field("proposal_stage_id", "Etapa da proposta enviada", "Para onde vai a lead quando a proposta segue por email.",
            options?.stages.filter((s) => !s.is_won) ?? [], value.proposal_stage_id ? undefined : stageName(auto?.proposalStageId))}
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor="followup-hours">Prazo de seguimento (horas)</Label>
            <Input
              id="followup-hours"
              type="number"
              min={1}
              step={1}
              placeholder="48"
              value={value.followup_hours ?? ""}
              onChange={(e) => {
                const next = { ...value }
                const n = Number(e.target.value)
                if (!e.target.value || !Number.isFinite(n) || n < 1) delete next.followup_hours
                else next.followup_hours = Math.round(n)
                onChange(next)
              }}
            />
            <p className="text-xs text-muted-foreground">
              Sem atividade aberta na lead, o comercial tem este tempo, desde a criação da proposta, para lhe dar
              seguimento (Relatórios → «Seguimento das propostas do site»). Com atividade, vale o prazo dela.
            </p>
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor="odoo-url">Endereço do Odoo</Label>
            <Input
              id="odoo-url"
              type="url"
              placeholder="https://filitermica.odoo.com"
              value={value.odoo_url ?? ""}
              onChange={(e) => {
                const next = { ...value }
                const v = e.target.value.trim()
                if (!v) delete next.odoo_url
                else next.odoo_url = v
                onChange(next)
              }}
            />
            <p className="text-xs text-muted-foreground">Para o botão «Abrir no Odoo» das propostas. Vazio = https://filitermica.odoo.com.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-border p-3">
          <div className="space-y-0.5">
            <Label>Utilizador da integração</Label>
            <p className="text-sm">
              {value.integration_user_name
                ? <>{value.integration_user_name} <span className="text-xs text-muted-foreground">(utilizador {value.integration_user_id} no Odoo)</span></>
                : <span className="text-muted-foreground">Ainda não detetado</span>}
            </p>
            <p className="text-xs text-muted-foreground">
              A conta com que a app escreve no Odoo — detetada pelas notas que ela publica nas leads («Simulação … criada no
              simulador», «Proposta … enviada»). As notas e mudanças de etapa desta conta não contam como seguimento: se a mesma
              pessoa também acompanha leads com ela, a integração deve ter um utilizador próprio no Odoo.
            </p>
            {detectError && <p className="text-xs text-destructive">{detectError}</p>}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={detectAgain} disabled={detecting || !shell?.isAdmin}>
            {detecting ? "A detetar…" : "Detetar outra vez"}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
